import { createContext, useState, useContext, useEffect, useRef, useCallback } from "react";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChat } from './ChatContext';
import { useAssistant } from "./AssistantContext";
import { useResource } from "./ResourceContext";
import { useSettings } from "./SettingsContext";
import { useSkills } from "./SkillsContext";
import { useSignalR } from "./SignalRContext";
import { useUserProfile } from "./UserProfileContext";
import { useAccess } from "./AccessContext";
import { useChatHistory } from "./ChatHistoryContext";
import { debugLog } from "../../../configs/loggingConfig";

const MessagesContext = createContext();

export const buildCreateChatOptions = (model, assistant) => {
    const options = {
        modelId: model.engine,
    };

    if (assistant?.assistantInstructionId) {
        options.assistantInstructionId = assistant.assistantInstructionId;
    }

    return options;
}

function MessagesProvider({ children }) {
    const apiServiceClient = useSymphonyApiService("maestro", "v3");

    // referenced context components
    const { chat, fetchChatId, chatId, systemReservedTokens, isChatInContext, chatIdRef, silentLoadChats } = useChat();
    const { assistant, assistantTokenCount } = useAssistant();
    const { dataSources, resourceTokenCount, enabledDataStores, setAddMessage } = useResource();
    const { model, settings, modelEncoding } = useSettings();
    const { getEnabledSkillIds, skillTokenCount } = useSkills();
    const { canSendMessage } = useAccess();
    const { loadChats } = useChatHistory();

    const { addMappingMethod } = useSignalR();
    const { userProfile } = useUserProfile();

    // data
    const [messages, setMessages] = useState([]);
    const [requestedTokens, setRequestedTokens] = useState(0);
    const SELF_MESSAGE_ID = "00000000-0000-0000-0000-000000000000";
    const ASSISTANT_MESSAGE_ID = "00000000-0000-0000-0000-000000000001";
    const [messageTokenCount, setMessageTokenCount] = useState(0);
    const [chatSuggestions, setChatSuggestions] = useState([]);
    const [chatQuickActions, setChatQuickActions] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [messageText, setMessageText] = useState("");

    // statuses
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [assistantThinking, setAssistantThinking] = useState(false);
    const [sendSuccess, setSendSuccess] = useState(false);
    const [loadingAuditLog, setLoadingAuditLog] = useState(false);

    // refs for signalR
    const messagesRef = useRef();

    // Function to add a message directly to the chat (used by ResourceContext for file notifications)
    const addMessage = useCallback((message) => {
        setMessages(prevMessages => [...prevMessages, message]);
    }, []);

    // Register the addMessage function with ResourceContext
    useEffect(() => {
        if (setAddMessage) {
            setAddMessage(() => addMessage);
        }
    }, [setAddMessage, addMessage]);

    // Loading all messages for the current chat.
    const loadMessages = async (existingChatId = null, force = false) => {

        // Load from the predetermined chat ID, and default to the current one in context if not.
        let loadId = existingChatId ?? chatId;
        if (loadId && (force || !assistantThinking)) {
            try {
                setLoadingMessages(true);
                let messages = await apiServiceClient.Chats.getChatMessages(loadId)

                if (!messages || messages.length === 0) {
                    setMessages([]);
                } else {
                    const sortedMessages = messages.sort((a, b) => new Date(a.MessageDate) - new Date(b.MessageDate));
                    setMessages(sortedMessages);

                    //initial function passes 0 in it and goes through another function, originally setChatRecommendationsTimer(0)
                    generateChatSuggestions();
                    fetchChatQuickActions();
                }
            } catch (error) {
                debugLog.error("[MESSAGE/LOAD] Error getting chat");
            } finally {
                setLoadingMessages(false);
            }
        }
        else {
            return;
        }
    }

    // Sends message to the current chat.
    // If given, messageTextOverride is the text that will show in the chat message, instead of message
    // If skipUserMessage is true, don't add user message to UI (for regeneration scenarios)
    const sendMessage = async (message, messageTextOverride = null, skipUserMessage = false) => {

        // set assistant thinking state
        setAssistantThinking(true);
        clearChatSuggestions();
        
        // Only add user message to UI if not skipping
        if (!skipUserMessage) {
            setThinkingConversation(messageTextOverride ?? message);
        }
        setSendSuccess(false);

        const opt = buildCreateChatOptions(model, assistant);

        // get chat Id of existing chat, or create one if it does not exist
        let chatId;
        try {
            chatId = await fetchChatId(opt);
        }
        catch (error) {
            debugLog.error("[MESSAGE/SEND] Failed to create or fetch chat:", error);
            setAssistantThinking(false);
            return;
        }

        let mappedDataSources = dataSources
            .filter((ds) => ds.included)
            .map((ds) => ds.dataSourceId);

        try {
            const sendMessageResponse = await apiServiceClient.Messages.sendMessage(
                chatId,
                message,
                assistant,
                model,
                settings,
                mappedDataSources,
                getEnabledSkillIds(),
                messageTextOverride,
                enabledDataStores.map(x => x.id)
            );

            debugLog.log("[MESSAGE/SEND] API Response:", sendMessageResponse);

            // Async flows may return an accepted task payload before a finalized message object.
            // Do not mark this as a user-visible error; let SignalR/status updates or fallback reload finalize UI.
            if (!sendMessageResponse?.messageId) {
                debugLog.log("[MESSAGE/SEND] No immediate messageId. Waiting for async completion and reloading messages.");
                await loadMessages(chatId, true);
                setSendSuccess(true);
                return;
            }

            const responseMessage = sendMessageResponse;
            debugLog.log("[MESSAGE/RECEIVE] About to call receiveMessage with:", responseMessage);
            receiveMessage(responseMessage);
            setSendSuccess(true);
        } catch (error) {
            setErrorConversation(messageTextOverride ?? message);
        } finally {
            setAssistantThinking(false);
            //These are not awaited so that the task returns independent of success here.
            generateChatSuggestions();
            fetchChatQuickActions();
        }
    }
    // Helper function to update messages state with the correct messageId from the response
    const updateMessagesWithResponse = (messageId, message) => {
        const ind = messagesRef.current.findIndex(
            (x) => x.content.some((y) => y.text === message)
        );

        if (ind !== -1) {
            const newMessages = [...messagesRef.current];
            newMessages[ind].messageId = messageId;
            setMessages(newMessages);
        }
    };

    // // Helper function to update messages state with the correct messageId from the response
    // const updateMessagesWithId = (messageId) => {
    //     const idx = -1;
    //     for (let i = messagesRef.current.length - 1; i >= 0; i--) {
    //         if (messagesRef.current[i].messageId === SELF_MESSAGE_ID && messagesRef.current[i].role === "user") {
    //             // should be most recent user message
    //             idx = i;
    //             break;
    //         }
    //     }

    //     if (idx !== -1) {
    //         const newMessages = [...messagesRef.current];
    //         newMessages[idx].messageId = messageId;
    //         setMessages(newMessages);
    //     } else {
    //         debugLog.log(`[MESSAGE/UPDATE]: No last user message found`);
    //     }
    // };

    // Throwing a message back to the AI to regenerate it.
    const regenerateMessage = async (messageId, tuning = undefined) => {
        // Store the data of the prompt and the response, delete those messages via deleteMessage, 
        // and send an adjustment back via sendMessage.
        
        // Find the index of the message we're regenerating, + sanity check to make sure it's an assistant message.
        const messageIndex = messages.findIndex((msg) => msg.messageId === messageId && msg.role === "assistant");
        if (messageIndex === -1) return;
        
        // Find the immediately preceding user message - this would be the one that likely triggered the response. 
        let lastUserMessageIndex = -1;
        for (let i = messageIndex - 1; i >= 0; i--) {
            if (messages[i].role === "user") {
                lastUserMessageIndex = i;
                break;
            }
        }
        if (lastUserMessageIndex === -1) return;

        // Keep all messages up to (but not including) the user message we're regenerating from
        const newMessages = messages.slice(0, lastUserMessageIndex);

        // ...
        // A <- initial user msg
        // B <- response (regen)
        // C
        // D

        // Delete only the user/assistant pair starting from the user message
        const deletedMessages = [];
        for (let i = lastUserMessageIndex; i < messages.length; i++) {
            const msg = messages[i];
            if (msg.role === "user" || msg.role === "assistant") {
                deletedMessages.push(msg);
            }
        }
        
        const deletionPromises = deletedMessages.map((msg) => deleteMessage(msg.messageId));

        // Delete the messages from the backend
        try {
            await Promise.all(deletionPromises);

            // Prepare the text content from the message contents
            let lastUserMessageText = extractTextContent(messages[lastUserMessageIndex].content);
            let regenerateMessageText = extractTextContent(messages[messageIndex].content);

            // Redundant update to ensure that both the rerender happens and we get accurate values in time for sendMessage.
            messagesRef.current = newMessages;
            setMessages(newMessages);

            // Handle different tuning scenarios
            if (tuning && tuning !== "Just Try Again") {
                sendMessage(`Given the following user request and assistant response, rewrite the assistant response but ${tuning}. Do not write anything but the new version of the response, do not address that you're even rewriting the response. Just create a new verison of the response.:\n###start user request\n${lastUserMessageText}\n###end user request\n\n###start assistant response\n${regenerateMessageText}\n###end assistant response`);
            } else { 
                // "Just Try Again" or no tuning will just resend the message.
                sendMessage(lastUserMessageText);
            }
        } catch (error) {
            debugLog.error("[MESSAGE/REGEN] Error deleting messages:", error);
        }
    }

    // Deletes a given message.
    const deleteMessage = async (messageId) => {
        debugLog.log("[MESSAGE/DEL] Starting to delete messages, " + messageId);
        return await apiServiceClient.Messages.deleteMessage(messageId);
    }

    // Creates a list of messages that is cut off at a certain index.
    const truncateMessages = (messages, truncateIndex) => {
        // Keep all messages up to truncateIndex and append non-user/assistant messages
        return messages
            .slice(0, truncateIndex)
            .concat(messages.slice(truncateIndex).filter((message) => message.role !== "user" && message.role !== "assistant"));
    };

    // For editing or resending a message.
    const editMessage = async (messageId, newContent) => {
        // edit message runs on the user message, so can grab it and delete all
        const messageIndex = messages.findIndex(
            (msg) => msg.messageId === messageId
        );
        // Invalid message ID.
        if (messageIndex === -1) return;
        // Invalid chat ID.
        if (!chatId) {
            debugLog.error("[MESSAGE/EDIT] Error deleting messages, chat not found");
            return;
        }

        // Get the messages to be deleted. Do this FIRST!
        let deletedMessages = messages
            .slice(messageIndex)
            .filter((msg) => msg.role === "user" || msg.role === "assistant");
        
        // Delete the messages from the backend, ensure these all finish before sendMessage triggers.
        const deletionPromises = deletedMessages.map((message) => apiServiceClient.Messages.deleteMessage(message.messageId))
        await Promise.all(deletionPromises);

        // Truncate messages up to the edited one
        // This should be identical to removing the above deleted messages.
        // It might be a good idea to synchronize these two in the future to avoid slight differences.
        const newMessages = truncateMessages(messages, messageIndex);
        // Redundant update to ensure that both the rerender happens and we get accurate values in time for sendMessage.
        messagesRef.current = newMessages
        setMessages(newMessages);

        // Re-send the edited message
        sendMessage(newContent);
    }

    // Retrieves the audit log for a given message.
    const getAuditLog = async (messageId) => {
        if (loadingAuditLog) return;

        setLoadingAuditLog(true);
        debugLog.log("[MESSAGE/AUDIT] Grabbing audit trail...");
        let trail = await apiServiceClient.Messages.getMessageAuditTrail(messageId);
        setAuditLog(trail);
        debugLog.log("[MESSAGE/AUDIT] Returned from call.");
        debugLog.log("[MESSAGE/AUDIT]", trail);
        setLoadingAuditLog(false);
    }

    // When a message is recieved, this function is called.
    const receiveMessage = (message) => {
        debugLog.log("[MESSAGE/RECEIVE] Called with:", message);
        if (message.chatId !== chatIdRef.current) return;
        
        // Transform jsonContent to content format if needed
        if (message.jsonContent && !message.content) {
            try {
                message.content = message.jsonContent.map(jsonStr => {
                    const parsed = JSON.parse(jsonStr);
                    // Map the property names to match expected format
                    const transformedContent = {
                        type: parsed.Type?.toLowerCase() || parsed.type,
                        text: parsed.Text || parsed.text || "",
                        contentOrder: parsed.ContentOrder || parsed.contentOrder || 0,
                        messageContentId: parsed.messageContentId,
                        MessageId: parsed.MessageId
                    };
                    
                    // Only include image properties if ImageBytes has actual content
                    if (parsed.ImageBytes && parsed.ImageBytes.trim() !== "") {
                        transformedContent.ImageBytes = parsed.ImageBytes;
                        transformedContent.ImageTextDescription = parsed.ImageTextDescription;
                    }
                    
                    return transformedContent;
                });
                debugLog.log("[MESSAGE/RECEIVE] Transformed jsonContent to content:", message.content);
                debugLog.log("[MESSAGE/RECEIVE] Content details:", JSON.stringify(message.content, null, 2));
            } catch (error) {
                debugLog.error("[MESSAGE/RECEIVE] Error parsing jsonContent:", error);
                message.content = [];
            }
        }
        debugLog.log("[MESSAGE/RECEIVE] Message recieved:", message)
        if (message.chatId !== chatIdRef.current) return;
        // if the message was sent from current client, return
        if (messagesRef.current.filter(
            (x) =>
                JSON.stringify(x.content) === JSON.stringify(message.content) &&
                (x.messageId === SELF_MESSAGE_ID || message.messageId === x.messageId)
        ).length > 0
        ) {
            debugLog.log("[MESSAGE/RECEIVE] message sent by this client");
            const ind = messagesRef.current.findIndex(
                (x) =>
                    JSON.stringify(x.content) === JSON.stringify(message.content) &&
                    (x.messageId === SELF_MESSAGE_ID || message.messageId === x.messageId)
            )
            messagesRef.current[ind] = message
            setMessages([...messagesRef.current])
            return;
        }
        else {
            debugLog.log("[MESSAGE/RECEIVE] Message not sent by this client");
            
            let newMessages = [...messagesRef.current];
            if (!messagesRef.current.some(x => x.messageId === message.messageId)) {
                newMessages.push(message);
            }
            
            if (message.role === "user") {
                if (message.messageId === SELF_MESSAGE_ID) {
                    //Message was sent by this user
                    newMessages.push({
                        messageId: ASSISTANT_MESSAGE_ID,
                        role: "assistant",
                        content: [{ type: "text", contentOrder: 0, text: "" }],
                        isLoading: true,
                    });
                }
                else {
                    //Message was sent by another user in shared chat
                    newMessages.push({
                        messageId: ASSISTANT_MESSAGE_ID,
                        role: "assistant",
                        content: [{ type: "text", contentOrder: 0, text: "" }],
                        isLoading: true,
                    });
                }
                
                // debugLog.log("[MESSAGE/RECEIVE] Message sent by user");
                // updateMessagesWithResponse(message.messageId, message);
            }
            else if (message.role === "assistant") {
                //Message sent by assistant
                debugLog.log("[MESSAGE/RECEIVE] Message sent by assistant");
                // updateMessagesWithId(message.replyToId);
                // updateMessagesWithResponse(message.messageId, message);
            }

            // organize messages by most recent
            setMessages(newMessages.filter((x) => x !== undefined && x?.isLoading !== true));
            debugLog.log("[MESSAGE/RECEIVE] Setting messages...");
        }
    }

    const receiveMessageStatus = async (statusMessage) => {
        debugLog.log("[MESSAGE/STATUS] ", statusMessage);

        if (statusMessage.chatId !== chatIdRef.current) return;
        var newMessages = [...messagesRef.current];
        var loadingMessageInd;
        //If the provided statusMessage does not define what it is responding to
        if (
            statusMessage.respondingTo === undefined ||
            statusMessage.respondingTo === null
        ) {
            //Set loadingMessageInd to the last index of a loading message
            loadingMessageInd = newMessages.findLastIndex((x) => x.isLoading);
        } else {
            var respondingToIndex = newMessages.findLastIndex(
                (x) =>
                    JSON.stringify(x.content) ===
                    JSON.stringify(statusMessage.respondingTo.content)
            );
            respondingToIndex = respondingToIndex === -1 ? 0 : respondingToIndex;
            debugLog.log("[MESSAGE/STATUS] Responding to index:", respondingToIndex)

            // After finding the message we are to respond to, find the next loading message and target that.
            let nextLoadingMessage = newMessages.slice(respondingToIndex).findIndex((x) => x.isLoading);
            // In the event that there is no loading message afterwards, we should add one.
            if (nextLoadingMessage === -1) {
                newMessages = await setThinkingResponse(newMessages);
                loadingMessageInd = newMessages.length - 1;
            } else {
                loadingMessageInd = respondingToIndex + nextLoadingMessage
            }
            debugLog.log("[MESSAGE/STATUS] Loading message index:", loadingMessageInd)
        }

        if (loadingMessageInd === -1) return;

        newMessages[loadingMessageInd].statusMessage = statusMessage.content;

        setMessages(newMessages);
    }

    // Generates reccomendations for follow-up questions based on messages in this chat.
    const generateChatSuggestions = async () => {
        // Call to generate suggestions based on the current chat ID.
        debugLog.log("[MESSAGE/SUG] Generating chat reccomendations...");
        let recommendations = undefined
        let id = await fetchChatId();
        if (id) {
            recommendations = await apiServiceClient.Chats.getChatRecommendations(id);
        }
        else {
            debugLog.error("[MESSAGE/SUG] Unable to generate chat suggestions")
        }
        // If suggestions return, update them.
        if (recommendations) setChatSuggestions(recommendations);
    }

    // Removes current chat suggestions.
    const clearChatSuggestions = () => {
        setChatSuggestions([]);
        setChatQuickActions([]);
    }

    const fetchChatQuickActions = async () => {
        // Call to generate quick actions based on the current chat ID.
        debugLog.log("[MESSAGE/SUG] Generating chat quick actions...");
        let id = await fetchChatId();
        try {
            if (id) {
                let quickActions = await apiServiceClient.Chats.getChatQuickActions(id);
                setChatQuickActions([...quickActions]);
            } else {
                setChatQuickActions([]);
            }
        } catch (error) {
            debugLog.error("[MESSAGE/QUICKACTIONS] Unable to fetch quick actions.")
        }
    }

    // Helper function that sets a timer for when recommendations should be generated.
    const setChatRecommendationsTimer = (timeout) => {
        if (canSendMessage) {
            setTimeout(async () => {
                if (chatId) {
                    await generateChatSuggestions();
                    await fetchChatQuickActions();
                }
            }, timeout);
        }
    }

    // Helper function to extract only the text from a message.
    const extractTextContent = (messageContent) => {
        return messageContent
            .filter((contentItem) => contentItem.type === "text")
            .sort((a, b) => a.contentOrder - b.contentOrder)
            .map((contentItem) => contentItem.text)
            .join("\n");
    };

    // Helper function to set conversation to loading response
    const setThinkingConversation = async (txt = "") => {
        var cnv = [
            {
                messageId: SELF_MESSAGE_ID,
                role: "user",
                content: [{ type: "text", contentOrder: 0, text: txt }],
                isLoading: false,
                userProfileId: userProfile.userProfileId,
                messageDate: new Date().toISOString()
            },
            {
                messageId: ASSISTANT_MESSAGE_ID,
                role: "assistant",
                content: [],
                isLoading: true,
            },
        ];
        setMessages((current) => [...current, ...cnv]);
    }

    const setThinkingResponse = async (newMessages = null) => {
        var thinkingMsg = [
            {
                messageId: ASSISTANT_MESSAGE_ID,
                role: "assistant",
                content: [],
                isLoading: true,
            },
        ];

        //This covers the null case as well
        if (!Array.isArray(newMessages)) {
            setMessages((current) => [...current.filter((message) => !message.isLoading), ...thinkingMsg]);
        } else {
            setMessages([...newMessages.filter((message) => !message.isLoading), ...thinkingMsg]);
            return [...newMessages.filter((message) => !message.isLoading), ...thinkingMsg]
        }
    }

    // Helper function to set conversation to placeholder error response
    const setErrorConversation = async (txt = "") => {
        var errorConv = [
            {
                messageId: SELF_MESSAGE_ID,
                role: "user",
                content: [{ type: "text", contentOrder: 0, text: txt }],
                isLoading: false,
                userProfileId: userProfile.userProfileId,
                messageDate: new Date().toISOString(),
            },
            {
                messageId: ASSISTANT_MESSAGE_ID,
                role: "error",
                content: [
                    {
                        type: "text",
                        contentOrder: 0,
                        text: "Error sending message, please copy your message text, refresh the page, and try again.",
                    },
                ],
                isLoading: false,
            },
        ];
        setMessages((current) => [...current, ...errorConv])
    }

    // Helper function to add an assistant message if one is not present.
    const addTemporaryAssistantMessage = (message) => {
        // only if cnv does not have a message with messageId of 00000000-0000-0000-0000-000000000001
        if (messages.find((msg) => msg.messageId === ASSISTANT_MESSAGE_ID)) {
            return;
        }
        else {
            var cnv = [
                ...messages,
                {
                    messageId: ASSISTANT_MESSAGE_ID,
                    role: "assistant",
                    content: [
                        { type: "text", contentOrder: 0, text: message },
                    ],
                },
            ];
            setMessages(cnv);
        }
    }

    // messageTokenCount calculation
    useEffect(() => {
        let total = 0;
        const messageEncodings = messages.map(
            (message) =>
                message.messageContentEncodings?.[modelEncoding] ?? 0
        );
        if (messageEncodings) {
            messageEncodings.forEach((x) => {
                total += x;
            });
        }
        setMessageTokenCount(total);
    }, [messages])

    // Update messages based on chat object
    useEffect(() => {
        if (messages.length > 0 && chat && messages[0].chatId === chat?.chatId) {
            chat.messages = messages;
        }
        if (!(isChatInContext() && (chat?.messages?.length ?? 0) === 0)) {
            setMessages([...chat?.messages ?? []]);
            clearChatSuggestions();
            setChatRecommendationsTimer(10000);
        }
    }, [chat]);

    // upon first assistant message in a new chat, silent load chat history
    useEffect(() => {
        if (sendSuccess && messages.filter(x => x.role === "user").length <= 1) {
            debugLog.log("[MESSAGE] silent load chats")
            loadChats(true);
        }
    }, [sendSuccess]);

    // update ref for signalR
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    // global token calculation
    useEffect(() => {
        setRequestedTokens(
            systemReservedTokens +
            (messageTokenCount ?? 0) +
            (assistantTokenCount ?? 0) +
            (resourceTokenCount ?? 0) +
            // (modelTokenCount ?? 0) +
            (skillTokenCount ?? 0)
        );
    }, [messageTokenCount, assistantTokenCount, resourceTokenCount, skillTokenCount])

    // mapping methods to signalR
    useEffect(() => {
        addMappingMethod(
            {
                //This method will be called when a new message is received from the user. This message with have an empty GUID since it is not yet finalized in the database.
                TextName: "ReceiveMessage",
                Method: receiveMessage,
            }
        );
        addMappingMethod(
            {
                //This method will be called when a new message is received from the user. This message with have an empty GUID since it is not yet finalized in the database.
                TextName: "ReceiveMessageStatus",
                Method: receiveMessageStatus,
            }
        );
    }, [])

    return (
        <MessagesContext.Provider
            value={{
                // States
                messages,
                setMessages,
                messagesRef,
                messageTokenCount,
                requestedTokens,
                loadingMessages,
                setLoadingMessages,
                assistantThinking,
                setAssistantThinking,
                editingMessageId,
                setEditingMessageId,
                chatSuggestions,
                chatQuickActions,
                auditLog,
                loadingAuditLog,
                sendSuccess,
                messageText,
                setMessageText,

                // Functions
                getAuditLog,
                setThinkingConversation,
                addTemporaryAssistantMessage,
                setErrorConversation,
                loadMessages,
                sendMessage,
                regenerateMessage,
                deleteMessage,
                truncateMessages,
                editMessage,
                setChatRecommendationsTimer,
                clearChatSuggestions,
                generateChatSuggestions,
                fetchChatQuickActions,
                receiveMessage,
                setThinkingResponse
            }}
        >
            {children}
        </MessagesContext.Provider>
    );
}

// Hook to use the MessagesContext in a component
function useMessages() {
    const context = useContext(MessagesContext);
    if (context === undefined) {
        throw new Error(
            "useMessages must be used within a MessagesProvider"
        );
    }
    return context;
}

export { MessagesProvider, useMessages };
